import * as React from 'react'
import Box from '@mui/material/Box'
import ErrorIcon from '@mui/icons-material/Error'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import { useVendorWizard } from 'vendors/vendorWizard/VendorWizardContext'
import { useEnsurementRequest } from 'vendors/vendorWizard/selectCompanyStep/queries/useCompaniesMutations'
import { Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

type EnsurementProps = {
  onSelectNewCompany: () => void
}

const Ensurement: React.FC<EnsurementProps> = ({ onSelectNewCompany }) => {
  const { company, vendorUser } = useVendorWizard()
  const { t } = useTranslation()

  const [additionalInformation, setAdditionalInformation] = React.useState('')

  const { mutate: ensurementRequest, isPending: isLoading, isError, isSuccess } = useEnsurementRequest()

  const handleAdditionalInfoChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setAdditionalInformation(event.target.value)

  const handleSendEnsurementRequest = () => {
    const payload = {
      companyName: company?.name ?? '',
      companyBusinessId: company?.businessId ?? '',
      contactName: `${vendorUser.firstname} ${vendorUser.lastname}`,
      contactEmail: vendorUser.email.toLowerCase(),
      contactPhone: vendorUser.phone,
      additionalInformation: additionalInformation
    }
    ensurementRequest(payload)
  }

  if (isError) {
    return <Navigate to="/error" replace />
  }

  if (isSuccess) {
    return (
      <Box display="grid" gridTemplateColumns="48px 1fr" gap={2}>
        <CheckCircleIcon color="success" sx={{ height: 48, width: 48 }} />
        <Box>
          <Typography gutterBottom>{t('thank-you-for-your-report')}</Typography>
          <Typography>{t('ensurement-support-text')}</Typography>
        </Box>
      </Box>
    )
  }

  return (
    <Box display="grid" gridTemplateColumns="48px 1fr" gap={2}>
      <ErrorIcon color="error" sx={{ height: 48, width: 48 }} />
      <Box>
        <Typography sx={{ mt: 1 }}>{t('validation-failed-text')}</Typography>
        <Box sx={{ mt: 3 }}>
          <Typography variant="caption" color="textSecondary">
            {t('company')}
          </Typography>
          <Typography color="textSecondary">{`${company?.name} (${company?.businessId})`}</Typography>
          <Typography
            sx={{ cursor: 'pointer', width: 'max-content' }}
            color="primary"
            variant="body2"
            onClick={onSelectNewCompany}
          >
            {t('select-another-company')}
          </Typography>
        </Box>
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="textSecondary">
            {t('your-information')}
          </Typography>
          <Typography color="textSecondary">{`${vendorUser.firstname} ${vendorUser.lastname}`}</Typography>
          <Typography color="textSecondary">{vendorUser.email}</Typography>
          <Typography color="textSecondary">{vendorUser.phone}</Typography>
          <TextField
            sx={{ mt: 2 }}
            multiline
            rows={2}
            id="additional-info"
            label={t('additional-information')}
            variant="filled"
            fullWidth
            value={additionalInformation}
            onChange={handleAdditionalInfoChange}
          />
          <Button
            sx={{ mt: 2 }}
            variant="outlined"
            color="grey"
            onClick={handleSendEnsurementRequest}
            disabled={isLoading}
          >
            {t('send')}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

export default Ensurement
